import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { cva, type VariantProps } from "class-variance-authority"
import { ChevronDown, Checkmark } from "@carbon/icons-react"
import { cn } from "../../../lib/utils"

const selectTriggerVariants = cva(
  "flex w-full items-center justify-between gap-2 border outline-none transition-colors font-[var(--font-family--sans)] placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted bg-white",
  {
    variants: {
      variant: {
        /* border.solid-10, text.primary, focus = foreground border + ring */
        default:
          "border-border text-foreground hover:border-epic-neutral-9 focus:border-foreground focus:ring-1 focus:ring-ring data-[state=open]:border-foreground data-[state=open]:ring-1 data-[state=open]:ring-ring",
        /* border.destructive, focus = destructive border + ring */
        error:
          "border-destructive text-foreground hover:border-destructive/80 focus:border-destructive focus:ring-1 focus:ring-destructive/30 data-[state=open]:border-destructive data-[state=open]:ring-1 data-[state=open]:ring-destructive/30",
      },
      size: {
        small: "h-9 px-3 text-sm rounded-md",
        default: "h-11 px-4 text-sm rounded-lg",
        large: "h-12 px-4 text-base rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface SelectProps
  extends Omit<SelectPrimitive.SelectProps, "children">,
    VariantProps<typeof selectTriggerVariants> {
  placeholder?: string
  options: { value: string; label: string; disabled?: boolean; prefix?: React.ReactNode }[]
  className?: string
  startAdornment?: React.ReactNode
}

const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  ({ className, variant, size, placeholder, options, startAdornment, ...props }, ref) => {
    return (
      <SelectPrimitive.Root {...props}>
        <SelectPrimitive.Trigger
          ref={ref}
          className={cn(selectTriggerVariants({ variant, size }), className)}
        >
          {startAdornment}
          <span className="truncate min-w-0 flex-1 text-left">
            <SelectPrimitive.Value placeholder={placeholder ?? "Select an option"} />
          </span>
          <SelectPrimitive.Icon asChild>
            <ChevronDown
              size={16}
              className="text-muted-foreground shrink-0 transition-transform duration-200 [[data-state=open]_&]:rotate-180"
            />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={4}
            className="relative z-[9999] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-lg border border-border bg-white shadow-md animate-in fade-in-0 zoom-in-95"
          >
            <SelectPrimitive.Viewport className="p-1 max-h-40 overflow-y-scroll">
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={cn(
                    "relative flex w-full cursor-pointer select-none items-center gap-1.5 rounded-md px-3 py-2 text-xs text-foreground outline-none transition-colors",
                    "hover:bg-muted focus:bg-muted",
                    "data-[disabled]:pointer-events-none data-[disabled]:opacity-40",
                    "data-[state=checked]:font-medium data-[state=checked]:bg-muted"
                  )}
                >
                  {option.prefix}
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className="absolute right-3">
                    <Checkmark size={14} />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    )
  }
)
Select.displayName = "Select"

export { Select, selectTriggerVariants }